import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '@/lib/api';
import type { AuditLog } from '@/lib/types';
import { Card, PageHeader, Pill, Stat, cellStyle } from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { Select } from '@/components/Select';
import { useSort, SortTh } from '@/components/sortable';
import { dt } from '@/lib/format';
import { downloadCsv } from '@/lib/csv';

const METHOD_TONE: Record<string, { bg: string; color: string }> = {
  GET: { bg: 'var(--surface-2)', color: 'var(--ink-2)' },
  POST: { bg: 'var(--success-bg)', color: 'var(--success)' },
  PATCH: { bg: 'var(--warn-bg)', color: 'var(--warn)' },
  PUT: { bg: 'var(--warn-bg)', color: 'var(--warn)' },
  DELETE: { bg: 'var(--danger-bg)', color: 'var(--danger)' },
};

export function AuditPage() {
  const [action, setAction] = useState('');

  const actions = useQuery({
    queryKey: ['audit-actions'],
    queryFn: () => api.get<string[]>('/audit/actions'),
    retry: false,
  });
  const logs = useQuery({
    queryKey: ['audit', action],
    queryFn: () => api.get<AuditLog[]>(`/audit?limit=300${action ? `&action=${encodeURIComponent(action)}` : ''}`),
    retry: false,
  });

  const forbidden =
    (logs.error instanceof ApiError && logs.error.status === 403) ||
    (actions.error instanceof ApiError && actions.error.status === 403);

  const list = logs.data ?? [];
  const sort = useSort(list, {
    action: (l) => l.action,
    actor: (l) => l.actorType + (l.actorId ?? ''),
    method: (l) => l.method,
    path: (l) => l.path,
    createdAt: (l) => l.createdAt,
  });

  if (forbidden) {
    return (
      <div>
        <PageHeader title="稽核日誌" subtitle="系統操作軌跡" />
        <Card title="權限不足">
          <div style={{ padding: 12, color: 'var(--ink-3)', fontSize: 13 }}>僅租戶管理者（TENANT_ADMIN）可檢視稽核日誌。</div>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title="稽核日誌"
        subtitle="所有敏感操作軌跡 · 防撈客 / 移轉主責 / 跨境阻擋皆留痕"
        right={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ width: 200 }}>
              <Select
                value={action}
                onChange={setAction}
                placeholder="全部操作類型"
                options={[{ value: '', label: '全部操作類型' }, ...(actions.data ?? []).map((a) => ({ value: a, label: a }))]}
              />
            </div>
            <button
              className="btn btn-ghost"
              disabled={list.length === 0}
              onClick={() => downloadCsv(`稽核日誌_${new Date().toISOString().slice(0, 10)}`, list.map((l) => ({ 時間: dt(l.createdAt), 操作: l.action, 操作者類型: l.actorType, 操作者: l.actorId ?? '', 方法: l.method, 路徑: l.path })))}
            >
              <Icon d={I.download} size={13} /> 匯出
            </button>
          </div>
        }
      />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="顯示筆數" value={String(list.length)} tone="primary" />
        <Stat label="操作類型" value={String(actions.data?.length ?? 0)} tone="ink" />
        <Stat label="寫入操作" value={String(list.filter((l) => l.method !== 'GET').length)} hint="POST/PATCH/DELETE" tone="warn" />
      </div>

      <Card title="操作軌跡" icon={<Icon d={I.history} size={14} style={{ color: 'var(--primary)' }} />}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <SortTh label="時間" sortKey="createdAt" sort={sort} />
              <SortTh label="操作" sortKey="action" sort={sort} />
              <SortTh label="操作者" sortKey="actor" sort={sort} />
              <SortTh label="方法" sortKey="method" sort={sort} />
              <SortTh label="路徑" sortKey="path" sort={sort} />
            </tr>
          </thead>
          <tbody>
            {sort.sorted.map((l) => {
              const m = METHOD_TONE[l.method] ?? METHOD_TONE.GET;
              return (
                <tr key={l.id}>
                  <td style={cellStyle} className="num">{dt(l.createdAt)}</td>
                  <td style={{ ...cellStyle, fontWeight: 600 }}>{l.action}</td>
                  <td style={cellStyle}>
                    <Pill bg="var(--surface-2)" color="var(--ink-2)">{l.actorType}</Pill>
                    {l.actorId && <span style={{ fontSize: 10.5, color: 'var(--ink-4)', marginLeft: 6 }}>{l.actorId.slice(0, 8)}</span>}
                  </td>
                  <td style={cellStyle}><Pill bg={m.bg} color={m.color}>{l.method}</Pill></td>
                  <td style={{ ...cellStyle, fontFamily: 'var(--mono, monospace)', fontSize: 11, color: 'var(--ink-2)' }}>{l.path}</td>
                </tr>
              );
            })}
            {list.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={5}>尚無稽核紀錄</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
